// W36: thin wrapper over the generated broadcasts client.
//
// W2: broadcasts service. A broadcast sends one template to a recipient list,
// throttled. The runner filters opted-out contacts before sending.
//
// Types come from `./generated/broadcasts` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate broadcasts`); the request
// path stays on the house core so the envelope, cursor pagination,
// Idempotency-Key, token refresh and ApiError mapping behave exactly as
// before.

import { http } from './_core/request.js';
import type { DashboardStat } from './_core/dashboard.js';
import type { Envelope } from './_core/envelope.js';
import type { Page, PageMeta, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/broadcasts/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

// Platform-wide: `services/broadcasts`, like every service here, sets
// `JsonSerializerOptions.DefaultIgnoreCondition = WhenWritingNull`, so the
// nullable members of the types below (`scheduled_at`, `started_at`,
// `finished_at`) are ABSENT from the JSON rather than present as `null`. The
// generated types declare them `null | string` and required; read them
// defensively and never `in`-test them.

/**
 * `contact_tag` on this DTO is the tag the broadcast was addressed to, where it
 * was addressed to one. Recorded at create time: the audience is resolved to a
 * roll of recipients then, so it says what was asked for rather than who is
 * tagged now.
 */
export type Broadcast = gen.BroadcastDto & {
  /** `whatsapp` or `sms` (4 October 2026). An SMS broadcast has no number and no template. */
  channel?: BroadcastChannel;
  sms_body?: string | null;
  sms_kind?: SmsKind | null;
  /** The parts one message takes; each part is charged. */
  sms_segments?: number | null;
  /** An email broadcast's subject line, as written (tokens unfilled). */
  email_subject?: string | null;
  /** An email broadcast's text: paragraphs separated by a blank line. */
  email_body?: string | null;
  email_button_label?: string | null;
  email_button_url?: string | null;
  email_kind?: EmailKind | null;
  /**
   * Reached (4 October 2026), counted from the recipient rows on the list and on `GET /v1/broadcasts/{id}`'s
   * list sibling: `recipients_delivered` includes those who read it, `recipients_read` is WhatsApp's blue ticks.
   * The list carries both; the single read does not (use {@link getBroadcastReport}).
   */
  recipients_delivered?: number;
  recipients_read?: number;
  /**
   * Why the broadcast failed as a whole: `INSUFFICIENT_CREDITS` for a scheduled one the credit could not cover.
   * Null for one that ran, even when every recipient failed. `failure_message` comes only on the single read.
   */
  failure_code?: string | null;
  failure_message?: string | null;
  /** The broadcast this one resends to the people it missed (4 October 2026). */
  resent_from_id?: string | null;
  /**
   * Each person is sent the approved version of the template in their contact language, else `template_language`
   * (1 November 2026). Each recipient's {@link BroadcastRecipient.template_language} says which version they got.
   */
  per_recipient_language?: boolean;
};

/** The channel a broadcast goes out on (4 October 2026): `email` added the same day as `sms`. */
export type BroadcastChannel = 'whatsapp' | 'sms' | 'email';
/** `marketing` reaches only contacts who agreed to SMS; `service` everyone who has not opted out of SMS. */
export type SmsKind = 'marketing' | 'service';
/**
 * `marketing` reaches only contacts who agreed to email; `service` everyone not
 * opted out of email. A contact without an email address is never in an email audience.
 */
export type EmailKind = 'marketing' | 'service';

export type BroadcastStats = gen.BroadcastStats;
export type BroadcastStatusCount = gen.BroadcastStatusCount;

/** `variables` is a JSON **string** of `{ var: value }` template substitutions. */
/**
 * Give exactly one audience: `contact_tag` (everyone carrying it who is still
 * opted in, which is how this product groups contacts), `segment_id` for a
 * saved rule, or `recipients` for an ad-hoc handful. A tag or a segment is
 * resolved server-side at create time, so the console must not pull an audience
 * into the browser and post it back, which is what it used to do and which
 * capped silently at a thousand.
 */
export type CreateBroadcastRequest = Omit<gen.CreateBroadcastRequest, 'phone_number_id' | 'template_code'> & {
  /** The WhatsApp number it goes from. Required for WhatsApp; not used for SMS. */
  phone_number_id?: string;
  /** The template. Required for WhatsApp; not used for SMS. */
  template_code?: string;
  /** `whatsapp` (default) or `sms`. SMS needs `sms_body` and is refused `SMS_UNAVAILABLE` until the platform has a provider. */
  channel?: BroadcastChannel;
  /** The SMS text. `{{contact.name|there}}` tokens are filled per recipient; the business name starts each message. */
  sms_body?: string;
  sms_kind?: SmsKind;
  /**
   * Email (`channel: 'email'`, 4 October 2026). `email_subject` and `email_body`
   * are required (400 `EMAIL_CONTENT_REQUIRED`); the body is plain text with
   * paragraphs separated by a blank line, `{{contact.name|there}}` tokens and
   * plain links. `recipients`, for email, are email addresses. Refused
   * `EMAIL_UNAVAILABLE` until the platform can send email.
   */
  email_subject?: string;
  email_body?: string;
  /** An optional button under the text. Needs `email_button_url` (absolute http(s), else `EMAIL_BUTTON_LINK`). */
  email_button_label?: string;
  email_button_url?: string;
  email_kind?: EmailKind;
  /**
   * Media for a template's IMAGE / VIDEO / DOCUMENT header: a public URL or an
   * uploaded Meta media id.
   *
   * Required for a media template. The example attached at approval is for
   * Meta's reviewers; every send has to carry its own, so a media template sent
   * without this is refused by Meta for every recipient. Declared here until
   * the generator is next run against the service.
   */
  header_media_url?: string;
  /**
   * Send each person the version in their language (1 November 2026, WhatsApp only). Each recipient is sent the
   * approved version of `template_code` in their contact language, else `template_language`: English takes the
   * first approved of `en_ZA`, `en_GB`, `en_US` and `en`, Portuguese `pt_PT` then `pt_BR`, any other language the
   * approved version whose base is its code. A version whose placeholders or header differ from
   * `template_language`'s is not used. Worked out when the broadcast is created and stored per recipient. The
   * price is per message, whatever the language. Off when omitted.
   */
  per_recipient_language?: boolean;
};

/** How many recipients are sent one version of the template (1 November 2026). `language` is Meta's code. */
export interface BroadcastLanguageCount {
  /** `en_US`, `af`, `pt_BR` ... */
  language: string;
  count: number;
}

export type BroadcastCreated = gen.BroadcastCreated & {
  channel?: BroadcastChannel;
  sms_segments?: number | null;
  /** Whether each person is sent the version in their language (1 November 2026). */
  per_recipient_language?: boolean;
  /** When they are: how many are sent each version, largest first. */
  languages?: BroadcastLanguageCount[] | null;
};

/**
 * `POST /v1/broadcasts` 400. W46: the house `{error:{code,message}}` envelope
 * (it used to be the bare `{"error":"recipients-required"}` string), so
 * `ApiError.code` reads `recipients_required`.
 */
export const RECIPIENTS_REQUIRED = 'recipients_required';

/** `POST /v1/broadcasts` 400: nobody carrying that tag is still opted in. */
export const TAG_EMPTY = 'tag_empty';

/** `POST /v1/broadcasts` 400: the segment matches nobody who can still be messaged. */
export const SEGMENT_EMPTY = 'segment_empty';

/**
 * `POST /v1/broadcasts` 400: the segment's rules narrow nothing, so it matches
 * every contact in the book. Refused rather than silently expanded to
 * "everybody", which is what a malformed rule document produces.
 */
export const SEGMENT_TOO_BROAD = 'segment_too_broad';

/** `POST /v1/broadcasts` 400: everyone in the audience has asked not to be messaged. */
export const AUDIENCE_ALL_OPTED_OUT = 'audience_all_opted_out';

/**
 * Meta on `GET /v1/broadcasts`. W46: the route took the house cursor page, so
 * all four members are real: `count` is this page, `total` is the org's row
 * count under the active filter, and `next_cursor` is absent on the last page.
 */
export interface BroadcastListMeta extends PageMeta {
  /** The org's broadcast count under the active filter: safe to render as "N broadcasts". */
  total?: number;
}

export interface BroadcastListPage extends Page<Broadcast> {
  meta?: BroadcastListMeta;
}

/** Query for {@link listBroadcasts}. */
export interface BroadcastListOpts extends PageOpts {
  /** Broadcast status **code**: `scheduled` | `running` | `completed` | `cancelled` | `failed`. */
  status?: string;
  /**
   * Row offset, for a console showing numbered pages. Takes precedence over
   * `cursor`; use the cursor to walk the whole list instead.
   */
  offset?: number;
}

// ── Operations ─────────────────────────────────────────────────────────────

/**
 * The org's broadcasts, newest first. W46: `limit`, `cursor` and `status` are
 * bound and honoured; before W46 the route took no parameters at all and
 * hard-capped at 200 rows, so older broadcasts were unreachable and
 * `meta.total` reported the page size. `paginate()` now walks the whole list.
 */
export function listBroadcasts(opts?: BroadcastListOpts): Promise<BroadcastListPage> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.status !== undefined) query.status = opts.status;
  return http.get<BroadcastListPage>('/v1/broadcasts', { query });
}

export function getBroadcast(id: string): Promise<Envelope<Broadcast>> {
  return http.get<Envelope<Broadcast>>(`/v1/broadcasts/${encodeURIComponent(id)}`);
}

export function createBroadcast(req: CreateBroadcastRequest): Promise<Envelope<BroadcastCreated>> {
  return http.post<Envelope<BroadcastCreated>>('/v1/broadcasts', { body: req });
}

/**
 * Start a broadcast. Its estimated cost is held in the wallet, so credit another
 * broadcast is still sending with cannot pay for it twice; the hold shrinks as
 * each message is charged and the rest is given back when it ends. Refused with
 * 402 `INSUFFICIENT_CREDITS` (estimate, what is free, the shortfall) when the
 * credit not held elsewhere cannot cover it. `force: true` starts it with no hold
 * and sends as far as the credit goes.
 */
/**
 * Starts a broadcast. Refused with 402 `INSUFFICIENT_CREDITS` when the credit
 * cannot be held, and with 409 `NUMBER_DAILY_LIMIT` (with `recipients`,
 * `remaining`, `limit`, `tier` and `resets_at`) when there are more recipients
 * than the number may still message today under WhatsApp's limit.
 * `allowOverLimit` starts it anyway and reaches as many as the limit allows;
 * `force` skips both checks.
 */
export function startBroadcast(id: string, opts?: { force?: boolean; allowOverLimit?: boolean }): Promise<void> {
  const query: Record<string, string> = {};
  if (opts?.force) query.force = 'true';
  if (opts?.allowOverLimit) query.allow_over_limit = 'true';
  return http.post<void>(`/v1/broadcasts/${encodeURIComponent(id)}/start`, Object.keys(query).length ? { query } : undefined);
}

export function cancelBroadcast(id: string): Promise<void> {
  return http.post<void>(`/v1/broadcasts/${encodeURIComponent(id)}/cancel`);
}

export function getBroadcastStats(id: string): Promise<Envelope<BroadcastStats>> {
  return http.get<Envelope<BroadcastStats>>(`/v1/broadcasts/${encodeURIComponent(id)}/stats`);
}

/** Campaign summary. Counts runs that actually started, never drafts. */
export type BroadcastsDashboardSummary = gen.BroadcastsDashboardSummary & DashboardStat & {
  /**
   * Across WhatsApp, SMS and email in the window (4 October 2026): everyone a message was handed over for, those
   * it reached (read included), those who read it (WhatsApp only), those it failed for, and the delivery rate as a
   * percentage of `recipients_attempted` (null when nothing was attempted).
   */
  recipients_attempted?: number;
  recipients_delivered?: number;
  recipients_read?: number;
  recipients_failed?: number;
  delivered_rate?: number | null;
};

/** `POST /v1/broadcasts/{id}/start` 409: it has already run, or is not in a state that can start (4 October 2026). */
export const BROADCAST_NOT_STARTABLE = 'BROADCAST_NOT_STARTABLE';
/** `POST /v1/broadcasts/{id}/cancel` 409: it has already finished, so there is nothing to cancel. */
export const BROADCAST_FINISHED = 'BROADCAST_FINISHED';
/** `POST /v1/broadcasts/{id}/resend-failed` 409: it is still sending. */
export const BROADCAST_NOT_FINISHED = 'BROADCAST_NOT_FINISHED';
/** `POST /v1/broadcasts/{id}/resend-failed` 409: nobody it missed can be reached by trying again. */
export const NOTHING_TO_RESEND = 'NOTHING_TO_RESEND';

/** Campaigns sent, this window vs last, and recipients reached in the window. */
export function getBroadcastSummary(windowHours?: number): Promise<Envelope<BroadcastsDashboardSummary>> {
  return http.get<Envelope<BroadcastsDashboardSummary>>('/v1/broadcasts/summary', {
    query: windowHours === undefined ? undefined : { window_hours: windowHours }
  });
}

/** One recipient of a broadcast, with the reason it failed where it did. */
export type BroadcastRecipient = Omit<gen.BroadcastRecipientDto, 'contact_msisdn'> & {
  /**
   * The machine-readable reason a send failed for good: channel-wa's
   * `error.code` (`INSUFFICIENT_CREDITS`, `PHONE_NOT_FOUND`, `ACCOUNT_NOT_PAYABLE`,
   * `WINDOW_CLOSED`, `META_REFUSED` ...) or the worker's `DISPATCH_GAVE_UP`.
   * `error` keeps the sentence. Declared here until the generator is next run
   * against the service.
   */
  error_code: string | null;
  /** How many times the sending worker actually tried this row. */
  attempts: number;
  /** When the worker handed this row to the send queue; null until it did. */
  dispatched_at: string | null;
  /** The number; null for a recipient of an email broadcast (4 October 2026). */
  contact_msisdn: string | null;
  /**
   * The address, for a recipient of an email broadcast. A bounce reads `failed`
   * with `error_code` `BOUNCED`.
   */
  contact_email?: string | null;
  /** When a WhatsApp recipient read it (4 October 2026). Read is WhatsApp's only. */
  read_at?: string | null;
  /**
   * The version of the template this person is sent, when the broadcast sends each person the version in their
   * language (1 November 2026). Absent means the broadcast's own `template_language`.
   */
  template_language?: string | null;
};

/**
 * What a broadcast came to (4 October 2026), counted from its recipients. `attempted` is everyone a message was
 * handed over for; the rates are over it, so people left out for consent never lower a delivery rate.
 * `awaiting_report` were sent with no delivery report yet (a phone that is off can take hours).
 */
export interface BroadcastReport {
  broadcast_id: string;
  channel: BroadcastChannel;
  started_at: string | null;
  finished_at: string | null;
  total: number;
  queued: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  filtered: number;
  cancelled: number;
  attempted: number;
  delivered_rate: number | null;
  /** WhatsApp only; null for SMS and email, which report delivery and not opening. */
  read_rate: number | null;
  failed_rate: number | null;
  awaiting_report: number;
  reports_read: boolean;
  /** Failures (and consent filtering) grouped by reason, largest first, with one example sentence each. */
  failures: { code: string; status: string; count: number; example: string | null }[];
  first_sent_at: string | null;
  last_sent_at: string | null;
  last_delivered_at: string | null;
  last_read_at: string | null;
  /** People who turned the business away after it: marked as spam, unsubscribed, or replied STOP. */
  opted_out_after: { total: number; spam: number; unsubscribed: number; stop: number };
  /** Why the broadcast failed as a whole (a scheduled one the credit could not cover). */
  failure_code: string | null;
  failure_message: string | null;
  /** The broadcast this one resends to the people it missed. */
  resent_from_id: string | null;
  estimated_cost_minor: number | null;
  currency: string | null;
}

/** The funnel, rates and grouped failures of one broadcast. */
export function getBroadcastReport(id: string): Promise<Envelope<BroadcastReport>> {
  return http.get<Envelope<BroadcastReport>>(`/v1/broadcasts/${encodeURIComponent(id)}/report`);
}

/** Every recipient and what became of them, as a CSV file (phone, email, status, reason, times). */
export function downloadBroadcastRecipients(id: string): Promise<Blob> {
  return http.getBlob(`/v1/broadcasts/${encodeURIComponent(id)}/recipients.csv`);
}

/**
 * A new broadcast with the same message to the people this one failed to reach for a reason a second try can fix
 * (4 October 2026). People who cannot be reached this way (not on WhatsApp, a bounced address, no consent) are
 * left out and counted as `skipped`. It is created, not started. Refused 409 `BROADCAST_NOT_FINISHED` or
 * `NOTHING_TO_RESEND`.
 */
export function resendFailed(id: string): Promise<Envelope<{ id: string; recipients_total: number; skipped: number; resent_from_id: string }>> {
  return http.post(`/v1/broadcasts/${encodeURIComponent(id)}/resend-failed`);
}

/** Delivery across broadcasts by South African day and channel (4 October 2026). */
export interface BroadcastDelivery {
  days: number;
  since: string;
  channel: BroadcastChannel | null;
  totals: {
    channel: BroadcastChannel; attempted: number; delivered: number; read: number | null; failed: number; awaiting_report: number;
    delivered_rate: number | null; read_rate: number | null; failed_rate: number | null;
  }[];
  by_day: { day: string; channel: BroadcastChannel; attempted: number; delivered: number; read: number | null; failed: number; awaiting_report: number }[];
  failures: { channel: BroadcastChannel; code: string | null; count: number }[];
}

/**
 * Delivery by day for `days` (1 to 366, default 30), for one channel or all. Before the service fix of 4 October
 * 2026 the all-channels form (no `channel`) answered 500 (Postgres could not type the null parameter); the
 * customer console asks per channel, which works against either.
 */
export function getBroadcastDelivery(opts?: { days?: number; channel?: BroadcastChannel }): Promise<Envelope<BroadcastDelivery>> {
  return http.get<Envelope<BroadcastDelivery>>('/v1/broadcasts/delivery', {
    query: { ...(opts?.days ? { days: opts.days } : {}), ...(opts?.channel ? { channel: opts.channel } : {}) }
  });
}

export interface BroadcastRecipientOpts extends PageOpts {
  /** Recipient status CODE: `queued` | `sent` | `delivered` | `read` | `failed` | `filtered_opted_out` | `cancelled`. */
  status?: string;
  /** Row offset, for a numbered pager. Takes precedence over `cursor`. */
  offset?: number;
}

/**
 * Per-recipient outcome for a broadcast, failures first.
 *
 * The console could show that 40 of 500 recipients failed and could not say
 * which, or why: `BroadcastRecipient.Error` had been written since the runner
 * shipped and no endpoint returned recipients at all.
 */
export function listBroadcastRecipients(
  broadcastId: string,
  opts?: BroadcastRecipientOpts
): Promise<Page<BroadcastRecipient>> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.status) query.status = opts.status;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  return http.get<Page<BroadcastRecipient>>(
    `/v1/broadcasts/${encodeURIComponent(broadcastId)}/recipients`,
    { query }
  );
}

// ── The create wizard: preview and draft ───────────────────────────────────

/**
 * `POST /v1/broadcasts/preview`. Give the audience exactly as
 * {@link CreateBroadcastRequest} takes it; everything else is optional because
 * the wizard asks as it goes.
 *
 * Hand-written until the generator is next run against the service.
 */
export interface PreviewBroadcastRequest {
  /** Everyone carrying this tag who is still opted in. */
  contact_tag?: string;
  /** A saved segment's id. */
  segment_id?: string;
  /** An explicit list of msisdns. */
  recipients?: string[];
  /** Used to price the send. Without a template the answer carries no estimate. */
  template_code?: string;
  /** `marketing` | `utility` | `authentication` | `service`. Resolved from the catalogue when omitted. */
  template_category?: string;
  /**
   * The variables map as a JSON **string**, exactly as `createBroadcast` takes
   * it. When given, each sample contact comes back with its own resolved copy.
   */
  variables?: string;
  /** How many real recipients to return: 0-10, default 3. */
  sample_size?: number;
  /** `whatsapp` (default), `sms` or `email` (4 October 2026). */
  channel?: BroadcastChannel;
  /** The SMS text, for the sample and the price. */
  sms_body?: string;
  sms_kind?: SmsKind;
  /** The email's subject and text: `email_preview` comes back once both are given. */
  email_subject?: string;
  email_body?: string;
  email_button_label?: string;
  email_button_url?: string;
  email_kind?: EmailKind;
  /** The template's version the broadcast is sent in, as {@link CreateBroadcastRequest} takes it. */
  template_language?: string;
  /**
   * As {@link CreateBroadcastRequest.per_recipient_language}. With a `template_code`, the answer carries
   * {@link BroadcastPreview.languages}: how many of the people who will be messaged are sent each version.
   */
  per_recipient_language?: boolean;
}

/** One real recipient from the audience, with this broadcast's variables resolved for them. */
export interface BroadcastPreviewContact {
  msisdn: string;
  /** False for a pasted number that is not in the contact book: it gets the fallbacks. */
  known: boolean;
  name?: string | null;
  email?: string | null;
  locale?: string | null;
  /** The contact's custom fields, addressable as `{{contact.fields.key}}`. */
  fields: Record<string, string>;
  /** The request's `variables` with this contact's values filled in, as a JSON string. */
  variables?: string | null;
  /** What this person's SMS will say, tokens filled (SMS only). */
  sms_text?: string | null;
  /** This person's email subject and text, tokens filled (email only). */
  email_subject?: string | null;
  email_text?: string | null;
}

/** An email as it goes out (4 October 2026): who it is from, where replies go, and both renderings. */
export interface BroadcastEmailPreview {
  from: string;
  reply_to?: string | null;
  subject: string;
  text: string;
  /** The full HTML message. Untrusted for the console: render only in a sandboxed frame. */
  html: string;
}

/** Billing's quote for the reachable audience, against the wallet. */
export interface BroadcastPreviewEstimate {
  currency: string;
  recipients: number;
  total_minor: number;
  /** What the send can be paid from: the balance less credit held for a broadcast still sending. */
  balance_minor: number;
  shortfall_minor: number;
  /** Whether the wallet covers it. `startBroadcast` refuses with 402 while this is false. */
  ok: boolean;
}

/** The refusal `createBroadcast` or `startBroadcast` would give for this draft. */
export interface BroadcastPreviewRefusal {
  /**
   * `recipients_required` | `tag_empty` | `segment_empty` | `segment_too_broad`
   * | `audience_all_opted_out` | `INSUFFICIENT_CREDITS`.
   */
  code: string;
  message: string;
  /** Present on `INSUFFICIENT_CREDITS` only. */
  estimated_minor?: number;
  available_minor?: number;
  shortfall_minor?: number;
  currency?: string;
  recipients?: number;
}

/**
 * What a broadcast would be, before it is one.
 *
 * `matched` is everyone the audience names, `opted_out` is how many of those
 * asked not to be messaged, and `reachable` is the difference: the number that
 * would actually be queued and the number the estimate prices.
 */
export interface BroadcastPreview {
  /** `tag` | `segment` | `list` | `none`. */
  source: string;
  matched: number;
  opted_out: number;
  reachable: number;
  template_category?: string | null;
  channel?: BroadcastChannel;
  /** The SMS as it goes out, with the business name and (marketing) the opt-out line. */
  sms_preview?: string | null;
  /** The parts each SMS takes; each part is charged. */
  sms_segments?: number | null;
  /** The email as it goes out; null until a subject and a text are given, or while email is down. */
  email_preview?: BroadcastEmailPreview | null;
  sample: BroadcastPreviewContact[];
  estimate?: BroadcastPreviewEstimate | null;
  /** Absent when there is nothing in the way of sending. */
  refusal?: BroadcastPreviewRefusal | null;
  /**
   * With `per_recipient_language` and a template (1 November 2026): how many of the people who will be messaged
   * are sent each version, largest first, by the code `createBroadcast` stores it with. The estimate is the same
   * either way: the price is per message.
   */
  languages?: BroadcastLanguageCount[] | null;
}

/**
 * Resolve the audience, count who has opted out, quote the send against the
 * wallet, and name the refusal create or start would give. Writes nothing.
 *
 * The console used to answer three of those four questions itself: it counted a
 * tag from the tag list's own `opted_in` column, priced the audience with its
 * own estimate call, and learnt about the wallet only when `startBroadcast`
 * came back 402, which is after the broadcast had been created. This runs the
 * service's own audience resolution, so what the person reads before pressing
 * Send is what will be queued.
 */
export function previewBroadcast(req: PreviewBroadcastRequest): Promise<Envelope<BroadcastPreview>> {
  return http.post<Envelope<BroadcastPreview>>('/v1/broadcasts/preview', { body: req });
}

/**
 * A per-recipient token, usable as a variable value in
 * {@link CreateBroadcastRequest.variables}.
 *
 * A broadcast carries one set of values for the whole audience, so `Hi {{1}}`
 * could only ever greet everyone by the same name. A value may instead read
 * `{{contact.name|there}}`, and the runner resolves it per recipient as the
 * message goes out, using the contact as it stands then. The text after `|` is
 * what goes out when the contact has nothing there, and it matters: WhatsApp
 * refuses an empty template parameter, so a missing first name without a
 * fallback fails that person's send rather than merely reading oddly.
 *
 * Readable fields: `name`, `email`, `locale`, `msisdn`, and anything under
 * `fields.` (a contact's custom fields).
 */
export const CONTACT_FIELDS = ['name', 'email', 'locale', 'msisdn'] as const;

/** The broadcast this person is part-way through writing, or `null`. */
export interface BroadcastDraft {
  /** The console's own wizard state. Opaque to the service. */
  state: Record<string, unknown>;
  updated_at: string;
}

/**
 * The saved draft, or `data: null` when there is none.
 *
 * One per person per organisation: creating a broadcast is a four-step page and
 * everything typed on the way through used to live only in the browser, so a
 * reload lost it.
 */
export function getBroadcastDraft(): Promise<Envelope<BroadcastDraft | null>> {
  return http.get<Envelope<BroadcastDraft | null>>('/v1/broadcasts/draft');
}

/** Keep the broadcast being written. 400 `draft_too_large` past 64 KB. */
export function saveBroadcastDraft(state: Record<string, unknown>): Promise<Envelope<{ updated_at: string }>> {
  return http.put<Envelope<{ updated_at: string }>>('/v1/broadcasts/draft', { body: { state } });
}

/** Throw the part-written broadcast away. Called once it has been sent. */
export function discardBroadcastDraft(): Promise<void> {
  return http.delete<void>('/v1/broadcasts/draft');
}
